import { useState, useEffect, useMemo } from 'react';
import { db } from '../shared/firebase-config';
import { Select, Button } from '../components/ui';

const LIMIT = 500;
const TRUNCATE_LEN = 80;

interface UnsupportedRequest {
  id: string;
  userId: string;
  userEmail: string | null;
  userMessage: string;
  locale: string;
  createdAt: Date;
  rejectionType: string | null;
}

function truncate(s: string, max: number): string {
  if (!s) return '';
  return s.length <= max ? s : s.slice(0, max) + '…';
}

function escapeCsvCell(s: string): string {
  if (s.includes('"') || s.includes(',') || s.includes('\n')) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
}

function toDate(createdAt: { toDate?: () => Date; seconds?: number } | Date | undefined): Date {
  if (!createdAt) return new Date(0);
  if (createdAt instanceof Date) return createdAt;
  if (typeof (createdAt as { toDate?: () => Date }).toDate === 'function') {
    return (createdAt as { toDate: () => Date }).toDate();
  }
  const s = (createdAt as { seconds: number }).seconds;
  return s != null ? new Date(s * 1000) : new Date(0);
}

export default function UnsupportedRequests() {
  const [items, setItems] = useState<UnsupportedRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [localeFilter, setLocaleFilter] = useState<string>('all');
  const [rejectionFilter, setRejectionFilter] = useState<string>('all');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    db.collection('chatbotUnsupportedRequests')
      .orderBy('createdAt', 'desc')
      .limit(LIMIT)
      .get()
      .then((snap) => {
        if (cancelled) return;
        const list: UnsupportedRequest[] = [];
        snap.docs.forEach((doc) => {
          const d = doc.data();
          list.push({
            id: doc.id,
            userId: d.userId ?? '',
            userEmail: d.userEmail ?? null,
            userMessage: d.userMessage ?? '',
            locale: d.locale ?? '',
            createdAt: toDate(d.createdAt),
            rejectionType: d.rejectionType ?? null,
          });
        });
        setItems(list);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message ?? 'Failed to load unsupported requests');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      const matchLocale = localeFilter === 'all' || item.locale === localeFilter;
      const matchRejection = rejectionFilter === 'all' || item.rejectionType === rejectionFilter;
      return matchLocale && matchRejection;
    });
  }, [items, localeFilter, rejectionFilter]);

  const localeOptions = useMemo(() => {
    const set = new Set(items.map((i) => i.locale).filter(Boolean));
    return Array.from(set).sort();
  }, [items]);

  const handleExportCsv = () => {
    const headers = ['Date', 'User email', 'User message', 'Locale', 'Rejection type'];
    const rows = filteredItems.map((item) => [
      item.createdAt.toISOString(),
      item.userEmail ?? '',
      item.userMessage,
      item.locale,
      item.rejectionType ?? '',
    ]);
    const csvContent = [headers.map(escapeCsvCell).join(','), ...rows.map((r) => r.map(escapeCsvCell).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `unsupported-chatbot-requests-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const localeSelectOptions = [{ value: 'all', label: 'All locales' }, ...localeOptions.map((loc) => ({ value: loc, label: loc }))];
  const rejectionSelectOptions = [
    { value: 'all', label: 'All types' },
    { value: 'out_of_scope', label: 'Out of scope' },
    { value: 'create_new_on_existing', label: 'Create new on existing' },
  ];

  if (loading && items.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-2 border-brand-primary border-t-transparent" />
          <p className="mt-4 text-sm text-neutral-secondary">Loading unsupported requests...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4">
        <p className="text-sm text-red-800">Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-neutral-primary">Unsupported Chatbot Requests</h2>
      <p className="text-sm text-neutral-secondary">
        User messages where the chatbot responded with &quot;not supported&quot; (out-of-scope or create-new-on-existing). Use this to improve the service.
      </p>

      <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-neutral-border bg-white p-5 shadow-card">
        <Select
          options={localeSelectOptions}
          value={localeFilter}
          onChange={(e) => setLocaleFilter(e.target.value)}
          className="w-44"
        />
        <Select
          options={rejectionSelectOptions}
          value={rejectionFilter}
          onChange={(e) => setRejectionFilter(e.target.value)}
          className="w-48"
        />
        <Button variant="primary" onClick={handleExportCsv}>
          Export to CSV
        </Button>
        <span className="text-sm text-neutral-secondary">
          Showing {filteredItems.length} of {items.length} (max {LIMIT})
        </span>
      </div>

      <div className="overflow-hidden rounded-2xl border border-neutral-border bg-white shadow-card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-border">
            <thead className="bg-neutral-border/30">
              <tr>
                <th className="px-4 py-2 text-left text-sm font-medium text-neutral-primary">Date</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-neutral-primary">User email</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-neutral-primary">User message</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-neutral-primary">Locale</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-neutral-primary">Type</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-border">
              {filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-neutral-secondary">
                    No unsupported requests found.
                  </td>
                </tr>
              ) : (
                filteredItems.map((item) => (
                  <tr key={item.id} className="transition-colors hover:bg-neutral-border/20">
                    <td className="px-4 py-2 text-sm text-neutral-primary whitespace-nowrap">
                      {item.createdAt.toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-sm text-neutral-primary">{item.userEmail ?? '—'}</td>
                    <td className="px-4 py-2 text-sm text-neutral-primary max-w-xs" title={item.userMessage}>
                      {truncate(item.userMessage, TRUNCATE_LEN)}
                    </td>
                    <td className="px-4 py-2 text-sm text-neutral-primary">{item.locale || '—'}</td>
                    <td className="px-4 py-2 text-sm text-neutral-primary">{item.rejectionType ?? '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
